import React, { useMemo, useState } from "react";
import axios from "axios";
import "./RegistrationForm.css";
import Select from "react-select";
import countryList from "react-select-country-list";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { toastSuccess, toastError } from "../Services/toaster";

const RegistrationForm = ({
  eventId,
  eventData,
  isMember,
  formData,
  formErrors,
  error,
  isLoading,
  updateForm,
  handleFormSubmit,
  addAccompanyingPerson,
  removeAccompanyingPerson,
  updateAccompanyingPerson,
  setStep,
}) => {
  const nationalityOptions = useMemo(() => countryList().getData(), []);

  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState("");

  const API_URL =
    process.env.REACT_APP_API_URL || "https://uatclubosapi.iworkkz.com";

  const goBack = () => {
    setStep("details");
  };

  /**
   * Convert title values to the format expected by API.
   *
   * UI can have:
   * Mr.
   * Mrs.
   * Ms.
   * Dr.
   *
   * API example accepts string, so we keep the value as entered.
   */
  const normalizeTitle = (title) => {
    return title || "";
  };

  /**
   * Convert member status to API boolean.
   */
  const getIsMember = () => {
    return formData.memberStatus === "member";
  };

  /**
   * Get member id.
   *
   * Depending on your OTP/member API, member_id may already exist
   * in formData. We also check common alternate names.
   */
  const getMemberId = () => {
    return (
      formData.member_id ||
      formData.memberId ||
      formData.user_id ||
      formData.userId ||
      0
    );
  };

  /**
   * Resolve the correct per-person price directly from the event's
   * real prices (eventData.member_price / eventData.guest_price,
   * mapped in EventRegistrationPage from apiEvent.member_price /
   * apiEvent.non_member_price).
   *
   * IMPORTANT: this must never read from formData.amount / .price /
   * .totalAmount / .registrationAmount — nothing in this app ever
   * writes those fields, which is why registrations were previously
   * always submitted to the backend with price: 0 regardless of
   * member/non-member status.
   */
  const getPriceFor = (isMemberFlag) => {
    if (!eventData) {
      return 0;
    }

    const price = isMemberFlag ? eventData.member_price : eventData.guest_price;

    return Number(price || 0);
  };

  /**
   * Build one registration object.
   *
   * Main registrant + accompanying persons are all sent
   * inside the "registrations" array.
   */
  const buildMainRegistration = () => {
    const isMemberValue = getIsMember();

    return {
      is_member: isMemberValue,
      member_id: isMemberValue ? Number(getMemberId()) || 0 : 0,
      guest_id: 0,

      title: normalizeTitle(formData.title),
      first_name: formData.firstName || "",
      last_name: formData.lastName || "",

      /**
       * IMPORTANT:
       * Do not generate order_id here.
       * Backend generates order_id.
       */
      price: getPriceFor(isMemberValue),

      currency: formData.currency || "USD",

      dietary_preference: formData.dietaryNote === "Yes" ? "yes" : "no",

      dietary_information:
        formData.dietaryNote === "Yes"
          ? formData.dietaryRestrictions || ""
          : "",

      email: formData.email || "",
      phone: formData.phone || "",
      nationality: formData.nationality || "",

      additional_information: formData.additionalInfo || "",
      remarks: formData.additionalInfo || "",

      vat_invoice_required: formData.vatInvoice === "yes",
    };
  };

  /**
   * Build accompanying person registration objects.
   */
  const buildAccompanyingRegistrations = () => {
    if (!Array.isArray(formData.accompanyingPersons)) {
      return [];
    }

    return formData.accompanyingPersons.map((person) => {
      const personIsMember =
        person.memberStatus === "member"
          ? true
          : person.memberStatus === "guest"
            ? false
            : getIsMember();

      return {
        is_member: personIsMember,

        member_id: personIsMember
          ? Number(
              person.member_id ||
                person.memberId ||
                person.user_id ||
                person.userId ||
                0,
            ) || 0
          : 0,

        guest_id: Number(person.guest_id || person.guestId || 0) || 0,

        title: person.title || "",
        first_name: person.firstName || "",
        last_name: person.lastName || "",

        price: getPriceFor(personIsMember),

        currency: person.currency || formData.currency || "USD",

        dietary_preference: person.dietaryNote === "Yes" ? "yes" : "no",

        dietary_information:
          person.dietaryNote === "Yes" ? person.dietaryRestrictions || "" : "",

        email: person.email || "",
        phone: person.phone || "",
        nationality: person.nationality || "",

        additional_information: person.additionalInfo || "",

        remarks: person.remarks || "",

        vat_invoice_required: person.vatInvoice === "yes",
      };
    });
  };

  /**
   * Validate required fields before API call.
   */
  const validateRegistration = () => {
    if (!formData.firstName?.trim()) {
      return "First name is required.";
    }

    if (!formData.lastName?.trim()) {
      return "Last name is required.";
    }

    if (!formData.email?.trim()) {
      return "Email is required.";
    }

    if (!formData.phone?.trim()) {
      return "Phone number is required.";
    }

    if (!eventId) {
      return "Event ID is missing. Please go back and select the event again.";
    }

    if (!Array.isArray(formData.accompanyingPersons)) {
      return null;
    }

    for (
      let index = 0;
      index < formData.accompanyingPersons.length;
      index += 1
    ) {
      const person = formData.accompanyingPersons[index];

      if (!person.email?.trim()) {
        return `Email is required for accompanying person ${index + 1}.`;
      }

      if (!person.firstName?.trim()) {
        return `First name is required for accompanying person ${index + 1}.`;
      }

      if (!person.lastName?.trim()) {
        return `Last name is required for accompanying person ${index + 1}.`;
      }

      if (!person.phone?.trim()) {
        return `Phone number is required for accompanying person ${index + 1}.`;
      }
    }

    return null;
  };

  /**
   * MAIN REGISTRATION API
   *
   * POST
   * /event-registrations/event/{event_id}
   *
   * Backend generates:
   * - id
   * - order_id
   * - payment_status
   * - order_status
   * - created_at
   * - updated_at
   *
   * We keep the complete response and send it to PaymentView.
   */
  const submitRegistration = async (e) => {
    e.preventDefault();

    if (registrationLoading) {
      return;
    }

    setRegistrationError("");

    const validationError = validateRegistration();

    if (validationError) {
      setRegistrationError(validationError);
      toastError(validationError);
      return;
    }

    try {
      setRegistrationLoading(true);

      const mainRegistration = buildMainRegistration();

      const accompanyingRegistrations = buildAccompanyingRegistrations();

      const registrations = [mainRegistration, ...accompanyingRegistrations];

      console.log(
        "Registration API Request:",
        JSON.stringify(
          {
            registrations,
          },
          null,
          2,
        ),
      );

      const response = await axios.post(
        `${API_URL}/event-registrations/event/${eventId}`,
        {
          registrations,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      console.log("Registration API Response:", response.data);

      /**
       * API returns:
       *
       * [
       *   {
       *      id: 3,
       *      event_id: 32,
       *      order_id: "2026091100003",
       *      price: "150.00",
       *      currency: "INR",
       *      ...
       *   }
       * ]
       */

      const registrationResponse = Array.isArray(response.data)
        ? response.data
        : response.data?.registrations || response.data?.data || [];

      if (!registrationResponse.length) {
        throw new Error(
          "Registration was created, but no registration details were returned by the server.",
        );
      }

      /**
       * First registration is the main registrant.
       */
      const mainRegistrationResponse = registrationResponse[0];

      const orderId = mainRegistrationResponse?.order_id || "";

      /**
       * Backend price is the source of truth.
       *
       * Example:
       * price: "150.00"
       */
      const backendPrice = Number(mainRegistrationResponse?.price || 0);

      const backendCurrency = mainRegistrationResponse?.currency || "INR";

      if (!orderId) {
        throw new Error(
          "Registration was created, but order ID was not returned by the server.",
        );
      }

      /**
       * Calculate total price from ALL returned registrations.
       *
       * This is important when accompanying persons
       * also have a price.
       */
      const totalRegistrationAmount = registrationResponse.reduce(
        (total, registration) => {
          return total + Number(registration?.price || 0);
        },
        0,
      );

      /**
       * Use backend total if available.
       * Otherwise use first registration price.
       */
      const finalAmount =
        totalRegistrationAmount > 0 ? totalRegistrationAmount : backendPrice;

      /**
       * Save registration information.
       *
       * This allows PaymentView and payment callbacks
       * to use the SAME order ID generated by backend.
       */
      const registrationPaymentData = {
        eventId: Number(eventId),

        orderId,

        amount: finalAmount,

        currency: backendCurrency,

        registrationId: mainRegistrationResponse?.id || null,

        registrations: registrationResponse,

        mainRegistration: mainRegistrationResponse,

        /**
         * Keep the original form as well.
         */
        formData,
      };

      console.log("Registration Payment Data:", registrationPaymentData);

      /**
       * Store temporarily so refresh/callback can still
       * recover the order information.
       */
      sessionStorage.setItem(
        "event_registration_payment",
        JSON.stringify(registrationPaymentData),
      );

      localStorage.setItem("event_registration_order_id", String(orderId));

      localStorage.setItem("event_registration_amount", String(finalAmount));

      localStorage.setItem("event_registration_currency", backendCurrency);

      toastSuccess(`Registration created successfully. Order ID: ${orderId}`);

      /**
       * IMPORTANT:
       *
       * We DO NOT call VNPay here.
       *
       * Registration API is called first.
       *
       * Then PaymentView opens.
       *
       * PaymentView will use this orderId when creating
       * the VNPay payment.
       */

      if (typeof handleFormSubmit === "function") {
        /**
         * Pass the complete registration/payment data
         * to the parent.
         *
         * Parent should use this to:
         * set the real registration state
         * setStep("payment")
         */
        handleFormSubmit(e, registrationPaymentData);
      } else {
        /**
         * Fallback if parent does not provide a handler.
         */
        setStep("payment");
      }
    } catch (err) {
      console.error("Create Registration Error:", err);

      const apiError =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to create registration. Please try again.";

      let message = "Failed to create registration.";

      if (Array.isArray(apiError)) {
        message = apiError.map((item) => item?.msg || item).join(", ");
      } else if (typeof apiError === "string") {
        message = apiError;
      }

      setRegistrationError(message);
      toastError(message);
    } finally {
      setRegistrationLoading(false);
    }
  };

  /**
   * The submit handler used by the form.
   *
   * We intentionally use our registration API handler here.
   */
  const handleSubmit = (e) => {
    submitRegistration(e);
  };

  return (
    <div className="evreg-form">
      <button type="button" className="evreg-back" onClick={goBack}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path
            d="M19 12H5M5 12L12 19M5 12L12 5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Back
      </button>

      <div className="evreg-panel">
        <div className="evreg-header">
          <div className="evreg-pill">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>

            {formData.memberStatus === "member" ? "Member" : "Guest"}
          </div>

          <div className="evreg-ornament">
            <span></span>
            <i></i>
            <span></span>
          </div>

          <h1 className="evreg-title">
            {formData.memberStatus === "member"
              ? "Confirm your details"
              : "Complete your registration"}
          </h1>

          <p className="evreg-subtitle">
            {formData.memberStatus === "member"
              ? "Please verify and complete your registration details."
              : "Please fill in all required fields to register for the event."}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* =========================
              SECTION 01 + 02
          ========================== */}

          <div className="evreg-row evreg-row-primary">
            {/* SECTION 01 */}
            <div className="evreg-col">
              <div className="evreg-section">
                <div className="evreg-section-head">
                  <span className="evreg-num">01</span>

                  <h3 className="evreg-section-title">Personal Information</h3>

                  <span className="evreg-dots"></span>
                  <span className="evreg-dot-end"></span>
                </div>

                <div className="evreg-fields">
                  {/* TITLE */}
                  <div className="evreg-field">
                    <label className="evreg-label">Title</label>

                    <select
                      className="evreg-input evreg-select"
                      value={formData.title || ""}
                      onChange={(e) => updateForm("title", e.target.value)}
                    >
                      <option value="">Select title</option>

                      <option value="Mr.">Mr.</option>

                      <option value="Ms.">Ms.</option>

                      <option value="Mrs.">Mrs.</option>

                      <option value="Dr.">Dr.</option>
                    </select>
                  </div>

                  {/* FIRST NAME */}
                  <div className="evreg-field">
                    <label className="evreg-label">
                      First Name <span className="req">*</span>
                    </label>

                    <input
                      className={`evreg-input ${
                        formErrors.firstName ? "has-error" : ""
                      }`}
                      type="text"
                      placeholder="First name"
                      value={formData.firstName || ""}
                      onChange={(e) => updateForm("firstName", e.target.value)}
                    />

                    {formErrors.firstName && (
                      <div className="evreg-error-text">
                        {formErrors.firstName}
                      </div>
                    )}
                  </div>

                  {/* LAST NAME */}
                  <div className="evreg-field">
                    <label className="evreg-label">
                      Last Name <span className="req">*</span>
                    </label>

                    <input
                      className={`evreg-input ${
                        formErrors.lastName ? "has-error" : ""
                      }`}
                      type="text"
                      placeholder="Last name"
                      value={formData.lastName || ""}
                      onChange={(e) => updateForm("lastName", e.target.value)}
                    />

                    {formErrors.lastName && (
                      <div className="evreg-error-text">
                        {formErrors.lastName}
                      </div>
                    )}
                  </div>

                  {/* EMAIL */}
                  <div className="evreg-field">
                    <label className="evreg-label">
                      Email <span className="req">*</span>
                    </label>

                    <input
                      className={`evreg-input ${
                        formErrors.email ? "has-error" : ""
                      }`}
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email || ""}
                      onChange={(e) => updateForm("email", e.target.value)}
                    />

                    {formErrors.email && (
                      <div className="evreg-error-text">{formErrors.email}</div>
                    )}
                  </div>

                  {/* PHONE */}
                  <div className="evreg-field">
                    <label className="evreg-label">
                      Phone Number <span className="req">*</span>
                    </label>

                    <PhoneInput
                      country="vn"
                      value={formData.phone || ""}
                      onChange={(phone) => updateForm("phone", phone)}
                      enableSearch
                      countryCodeEditable={false}
                      placeholder="Enter phone number"
                      inputClass={`evreg-phone-input ${
                        formErrors.phone ? "has-error" : ""
                      }`}
                      containerClass="evreg-phone-container"
                      buttonClass="evreg-phone-button"
                    />

                    {formErrors.phone && (
                      <div className="evreg-error-text">{formErrors.phone}</div>
                    )}
                  </div>

                  {/* NATIONALITY */}
                  <div className="evreg-field">
                    <label className="evreg-label">Nationality</label>

                    <Select
                      className="evreg-nationality-select"
                      classNamePrefix="evreg-select"
                      options={nationalityOptions}
                      placeholder="Select nationality"
                      isSearchable
                      isClearable
                      value={
                        nationalityOptions.find(
                          (option) => option.label === formData.nationality,
                        ) || null
                      }
                      onChange={(option) =>
                        updateForm("nationality", option?.label || "")
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 02 */}
            <div className="evreg-col">
              <div className="evreg-section">
                <div className="evreg-section-head">
                  <span className="evreg-num">02</span>

                  <h3 className="evreg-section-title">Dietary Requirements</h3>

                  <span className="evreg-dots"></span>
                  <span className="evreg-dot-end"></span>
                </div>

                <div className="evreg-fields">
                  <div className="evreg-field span-3">
                    <label className="evreg-label">
                      Dietary Restriction Note
                    </label>

                    <div className="evreg-status-cards">
                      <div
                        className={`evreg-status-card ${
                          formData.dietaryNote === "No" ? "is-active" : ""
                        }`}
                        onClick={() => updateForm("dietaryNote", "No")}
                        style={{
                          padding: "8px 16px",
                        }}
                      >
                        <div className="evreg-radio">
                          {formData.dietaryNote === "No" && (
                            <div className="evreg-radio-dot"></div>
                          )}
                        </div>

                        <span className="evreg-status-title">
                          No restrictions
                        </span>
                      </div>

                      <div
                        className={`evreg-status-card ${
                          formData.dietaryNote === "Yes" ? "is-active" : ""
                        }`}
                        onClick={() => updateForm("dietaryNote", "Yes")}
                        style={{
                          padding: "8px 16px",
                        }}
                      >
                        <div className="evreg-radio">
                          {formData.dietaryNote === "Yes" && (
                            <div className="evreg-radio-dot"></div>
                          )}
                        </div>

                        <span className="evreg-status-title">
                          Yes, I have restrictions
                        </span>
                      </div>
                    </div>

                    {formData.dietaryNote === "Yes" && (
                      <input
                        className="evreg-input"
                        type="text"
                        placeholder="e.g. Vegetarian, Gluten-free, Halal, etc."
                        value={formData.dietaryRestrictions || ""}
                        onChange={(e) =>
                          updateForm("dietaryRestrictions", e.target.value)
                        }
                        style={{
                          marginTop: "10px",
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* =========================
              SECTION 03
          ========================== */}

          <div className="evreg-col">
            <div className="evreg-section">
              <div className="evreg-section-head">
                <span className="evreg-num">03</span>

                <h3 className="evreg-section-title">Accompanying Persons</h3>

                <span className="evreg-dots"></span>
                <span className="evreg-dot-end"></span>

                <button
                  type="button"
                  className="evreg-add"
                  onClick={addAccompanyingPerson}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Add Person
                </button>
              </div>

              <div className="evreg-member-toggle">
                <button
                  type="button"
                  className={`evreg-toggle-btn ${
                    formData.memberStatus === "member" ? "active" : ""
                  }`}
                  onClick={() => updateForm("memberStatus", "member")}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <path d="M12 15v2m-6 4h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2zm10-10V7a4 4 0 0 0-8 0v4h8z" />
                  </svg>

                  <span>Member</span>
                </button>

                <button
                  type="button"
                  className={`evreg-toggle-btn ${
                    formData.memberStatus === "guest" ? "active" : ""
                  }`}
                  onClick={() => updateForm("memberStatus", "guest")}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>

                  <span>Guest</span>
                </button>
              </div>

              <div className="evreg-table">
                <div className="evreg-table-head">
                  <span>#</span>
                  <span>Email*</span>
                  <span>Title *</span>
                  <span>First Name *</span>
                  <span>Last Name *</span>
                  <span>Phone Number *</span>
                  <span>Nationality</span>
                  <span></span>
                </div>

                {formData.accompanyingPersons?.length === 0 ? (
                  <div className="evreg-table-empty">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>

                    <p>No accompanying persons added yet.</p>

                    <p className="muted">Click "Add Person" to add guests.</p>
                  </div>
                ) : (
                  formData.accompanyingPersons?.map((person, index) => (
                    <div key={person.id} className="evreg-table-row">
                      <span className="evreg-row-index">{index + 1}</span>

                      {/* EMAIL */}
                      <input
                        className="evreg-input"
                        type="email"
                        placeholder="Email"
                        value={person.email || ""}
                        onChange={(e) =>
                          updateAccompanyingPerson(
                            person.id,
                            "email",
                            e.target.value,
                          )
                        }
                      />

                      {/* TITLE */}
                      <select
                        className="evreg-input evreg-select"
                        value={person.title || ""}
                        onChange={(e) =>
                          updateAccompanyingPerson(
                            person.id,
                            "title",
                            e.target.value,
                          )
                        }
                      >
                        <option value="">Select title</option>

                        <option value="Mr">Mr</option>

                        <option value="Mrs">Mrs</option>

                        <option value="Ms">Ms</option>
                      </select>

                      {/* FIRST NAME */}
                      <input
                        className="evreg-input"
                        type="text"
                        placeholder="First name"
                        value={person.firstName || ""}
                        onChange={(e) =>
                          updateAccompanyingPerson(
                            person.id,
                            "firstName",
                            e.target.value,
                          )
                        }
                      />

                      {/* LAST NAME */}
                      <input
                        className="evreg-input"
                        type="text"
                        placeholder="Last name"
                        value={person.lastName || ""}
                        onChange={(e) =>
                          updateAccompanyingPerson(
                            person.id,
                            "lastName",
                            e.target.value,
                          )
                        }
                      />

                      {/* PHONE */}
                      <PhoneInput
                        country="vn"
                        value={person.phone || ""}
                        onChange={(phone) =>
                          updateAccompanyingPerson(person.id, "phone", phone)
                        }
                        enableSearch
                        countryCodeEditable={false}
                        placeholder="Phone number"
                        containerClass="evreg-person-phone-container"
                        inputClass="evreg-person-phone-input"
                        buttonClass="evreg-person-phone-button"
                        dropdownClass="evreg-person-phone-dropdown"
                        dropdownStyle={{
                          zIndex: 999999,
                        }}
                      />

                      {/* NATIONALITY */}
                      <Select
                        className="evreg-nationality-select"
                        classNamePrefix="evreg-select"
                        options={nationalityOptions}
                        placeholder="Nationality"
                        isSearchable
                        isClearable
                        value={
                          nationalityOptions.find(
                            (option) => option.label === person.nationality,
                          ) || null
                        }
                        onChange={(option) =>
                          updateAccompanyingPerson(
                            person.id,
                            "nationality",
                            option?.label || "",
                          )
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 99999,
                          }),
                        }}
                      />

                      {/* REMOVE */}
                      <button
                        type="button"
                        className="evreg-row-remove"
                        onClick={() => removeAccompanyingPerson(person.id)}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* =========================
              SECTION 04 + 05
          ========================== */}

          <div className="evreg-row evreg-row-split">
            {/* SECTION 04 */}
            <div className="evreg-col">
              <div className="evreg-section">
                <div className="evreg-section-head">
                  <span className="evreg-num">04</span>

                  <h3 className="evreg-section-title">VAT Invoice</h3>

                  <span className="evreg-dots"></span>
                  <span className="evreg-dot-end"></span>
                </div>

                <p
                  className="evreg-label"
                  style={{
                    marginBottom: 10,
                  }}
                >
                  Do you want to issue a VAT invoice?
                </p>

                <div className="evreg-radio-row">
                  <label
                    className={`evreg-radio-opt ${
                      formData.vatInvoice === "no" ? "is-active" : ""
                    }`}
                  >
                    <div className="evreg-radio">
                      {formData.vatInvoice === "no" && (
                        <div className="evreg-radio-dot"></div>
                      )}
                    </div>
                    <input
                      type="radio"
                      name="vatInvoice"
                      value="no"
                      checked={formData.vatInvoice === "no"}
                      onChange={(e) => updateForm("vatInvoice", e.target.value)}
                      style={{
                        display: "none",
                      }}
                    />
                    No
                  </label>

                  <label
                    className={`evreg-radio-opt ${
                      formData.vatInvoice === "yes" ? "is-active" : ""
                    }`}
                  >
                    <div className="evreg-radio">
                      {formData.vatInvoice === "yes" && (
                        <div className="evreg-radio-dot"></div>
                      )}
                    </div>
                    <input
                      type="radio"
                      name="vatInvoice"
                      value="yes"
                      checked={formData.vatInvoice === "yes"}
                      onChange={(e) => updateForm("vatInvoice", e.target.value)}
                      style={{
                        display: "none",
                      }}
                    />
                    Yes
                  </label>
                </div>
              </div>
            </div>

            {/* SECTION 05 */}
            <div className="evreg-col">
              <div className="evreg-section">
                <div className="evreg-section-head">
                  <span className="evreg-num">05</span>

                  <h3 className="evreg-section-title">
                    Additional Information
                  </h3>

                  <span className="evreg-dots"></span>
                  <span className="evreg-dot-end"></span>
                </div>

                <div className="evreg-fields">
                  <div className="evreg-field span-3">
                    <label className="evreg-label">
                      Remarks / Special Requests
                    </label>

                    <div className="evreg-textarea-wrap">
                      <textarea
                        className="evreg-input evreg-textarea"
                        placeholder="Any special requests or additional information?"
                        value={formData.additionalInfo || ""}
                        onChange={(e) =>
                          updateForm("additionalInfo", e.target.value)
                        }
                        rows={4}
                      />

                      <span className="evreg-leaf">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        >
                          <path d="M12 2L2 7l10 5 10-5-10-5z" />
                          <path d="M2 17l10 5 10-5" />
                          <path d="M2 12l10 5 10-5" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* API ERROR */}
          {(error || registrationError) && (
            <div className="evreg-form-error">{registrationError || error}</div>
          )}

          {/* ACTIONS */}
          <div className="evreg-actions">
            <button
              type="button"
              className="evreg-btn-secondary"
              onClick={goBack}
              disabled={registrationLoading}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 12H5M5 12L12 19M5 12L12 5" />
              </svg>
              Back
            </button>

            <button
              type="submit"
              className="evreg-btn-primary"
              disabled={registrationLoading || isLoading}
            >
              {registrationLoading || isLoading ? (
                <>
                  <span className="spinner"></span>
                  Creating Registration…
                </>
              ) : (
                <>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  Continue to Payment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationForm;
